import React, { useState, useRef, useEffect } from 'react'
import { Breadcrumbs, Tabs, CreateButton, DropDownMenu, DynamicEntityIndex, FlashMessage, GlobalLoader, InputField, Modal, ModalAlert, SaveFixedButton } from '@reactiveonline/reactive_admin_shared-packages'
import { dynamicTableItemRenderer, emailValidation } from '@reactiveonline/reactive_admin_shared-packages/utils'
import GlobalImports from '../shared/GlobalImports'

const initialNewUserState = {
  name: '',
  uid: '',
  email: ''
}

export default function Edit({
  appProps, administratorsCount, administratorIcon, administratorListColumns, administratorSelectedColumns, branchesPath, breadcrumbs, canCreateUsers, canDeleteAdministrators, canDeleteUsers, canEditAdministrator, canEditUser, canEditBranch, canEditWhitelistedIps, canInviteBranchAdministrator,
  canViewAdministrator, deleteAdministratorsPath, deleteUsersPath, getAdministratorsPath, inviteNewBranchAdminPath, branch, branchIcon, updateAdministratorPath, updatePath,
  userIcon, usersCount, usersPath, userListColumns, userSelectedColumns, reinvitePath
}) {
  const [saveButtonRef, setSaveButtonRef] = useState(null)
  const [branchName, setBranchName] = useState(branch.name)
  const [branchState, setBranchState] = useState(branch)
  const [whitelistedIps, setWhitelistedIps] = useState(branch.whitelistedIps)
  const [administrators, setAdministrators] = useState([])
  const [users, setUsers] = useState([])
  const [administratorsCountState, setAdministratorsCountState] = useState(administratorsCount)
  const [usersCountState, setUsersCountState] = useState(usersCount)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [showAddNewAdminModal, setShowAddNewAdminModal] = useState(false)
  const [newUser, setNewUser] = useState(initialNewUserState)
  const [newAdministratorEmail, setNewAdministratorEmail] = useState('')
  const [newAdministratorName, setNewAdministratorName] = useState('')
  const [newAdministratorUid, setNewAdministratorUid] = useState('')
  const [adminToBeEdited, setAdminToBeEdited] = useState(null)
  const [updatedName, setUpdatedName] = useState('')
  const [updatedUid, setUpdatedUid] = useState('')
  const [administratorToBeDeleted, setAdministratorToBeDeleted] = useState(null)
  const [loading, setLoading] = useState(loading)
  const [renderKey, setRenderKey] = useState(0)
  const [currentTab, setCurrentTab] = useState('users')

  const didMountRef = useRef(false)
  const flashMessageRef = useRef(null)

  useEffect(() => {
    if (didMountRef.current) {
      saveButtonRef.setSaveableStatus()
    } else {
      didMountRef.current = true
    }
  }, [branchState])

  const actions = []

  if (canDeleteUsers) {
    actions.unshift({ onClick: (event, item) => handleDeleteAdministratorClick(item.apiUid, event), icon: 'fa-trash' })
  }

  if (canEditUser) {
    actions.unshift({ onClick: (event, item) => handleEditUserClick(item.apiUid, event), icon: 'fa-pencil' })
  }

  if (canCreateUsers) {
    actions.unshift({ onClick: (event, item) => handleReinvite(item, event), icon: 'fa-envelope'})
  }

  const administratorActions = []

  if (canDeleteAdministrators) {
    administratorActions.unshift({ onClick: (event, item) => handleDeleteAdministratorClick(item.apiUid, event), icon: 'fa-trash' })
  }

  if (canEditAdministrator) {
    administratorActions.unshift({ onClick: (event, item) => handleEditAdministratorClick(item.apiUid, event), icon: 'fa-pencil' })
  }

  if (canInviteBranchAdministrator) {
    administratorActions.unshift({ onClick: (event, item) => handleReinvite(item, event), icon: 'fa-envelope'})
  }

  const handleBranchSave = () => {
    const { name, uid, internalName,branchLocation } = branchState
    const fd = new FormData()

    fd.append('branch[name]', name)
    fd.append('branch[uid]', uid)
    fd.append('branch[internal_name]', internalName)
    fd.append('branch[branch_location]', branchLocation)

    whitelistedIps.forEach((whitelistedIp, idx) => {
      const { id, ip, _destroy } = whitelistedIp

      if (id > 0) {
        fd.append(`branch[whitelisted_ips_attributes][${ idx }][id]`, id)
      }

      if (_destroy || (id > 0 && !ip)) {
        fd.append(`branch[whitelisted_ips_attributes][${ idx }][_destroy]`, true)
      } else {
        if (ip) {
          fd.append(`branch[whitelisted_ips_attributes][${ idx }][ip]`, ip)
        }
      }
    })

    saveButtonRef.setSavingStatus()

    Rails.ajax({
      type: 'PATCH',
      url: updatePath,
      dataType: 'json',
      data: fd,
      success: res => {
        saveButtonRef.setSuccessStatus()

        setBranchState(res.branch)
        setBranchName(res.branch.name)
        setWhitelistedIps(res.branch.whitelistedIps)
      },
      error: res => saveButtonRef.setErrorStatusWithMessage(res.errors)
    })
  }

  const verifyFields = () => {
    const { name, email } = newUser

    if (name === '' || !emailValidation(email)) {
      return true
    }

    return false
  }

  const closeCreateUserModal = () => {
    setNewUser(initialNewUserState)
    setShowCreateUserModal(false)
  }

  const closeAddNewAdminModal = () => {
    setNewAdministratorEmail('')
    setNewAdministratorName('')
    setNewAdministratorUid('')
    setShowAddNewAdminModal(false)
  }

  const inviteBranchAdministrator = () => {
    const fd = new FormData()
    fd.append('admin_email', newAdministratorEmail)
    fd.append('admin_name', newAdministratorName)
    fd.append('admin_uid', newAdministratorUid)

    closeAddNewAdminModal()
    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: inviteNewBranchAdminPath,
      dataType: 'json',
      data: fd,
      success: res => {
        flashMessageRef.current.show(res.message, 'success')
        setAdministratorsCountState(res.administratorsCount)
        setRenderKey(Math.random())
        setLoading(false)
      },
      error: res => {
        flashMessageRef.current.show(res.message, 'error')
        setLoading(false)
      }
    })
  }

  const createUser = () => {
    const { name, uid, email } = newUser

    const fd = new FormData()
    fd.append('user[name]', name)
    fd.append('user[email]', email)
    if (uid) {
      fd.append('user[uid]', uid)
    }

    setLoading(true)
    closeCreateUserModal()

    Rails.ajax({
      type: 'POST',
      url: usersPath,
      dataType: 'json',
      data: fd,
      success: res => {
        flashMessageRef.current.show(res.message, 'success')
        setUsersCountState(res.usersCount)
        setRenderKey(Math.random())
      },
      error: res => flashMessageRef.current.show(res.message, 'error'),
      complete: _ => setLoading(false)
    })
  }

  const handleUpdateWhitelistedIp = (whitelistedIpId, value) => {
    const relevantIpIndex = whitelistedIps.findIndex(el => el.id === whitelistedIpId)

    saveButtonRef.setSaveableStatus()

    setWhitelistedIps(prevState => [
      ...prevState.slice(0, relevantIpIndex),
      {
        ...prevState[relevantIpIndex],
        ip: value
      },
      ...prevState.slice(relevantIpIndex + 1)
    ])
  }

  const handleAddWhitelistedIp = () => {
    saveButtonRef.setSaveableStatus()

    setWhitelistedIps(prevState => [
      ...prevState,
      {
        id: Math.min(...whitelistedIps.map(el => el.id), 0) - 1,
        ip: '',
      }
    ])
  }

  const handleDeleteWhitelistedIp = whitelistedIpId => {
    const relevantIpIndex = whitelistedIps.findIndex(el => el.id === whitelistedIpId)

    saveButtonRef.setSaveableStatus()

    if (whitelistedIpId < 0) {
      setWhitelistedIps(prevState => [
        ...prevState.slice(0, relevantIpIndex),
        ...prevState.slice(relevantIpIndex + 1)
      ])
    } else {
      setWhitelistedIps(prevState => [
        ...prevState.slice(0, relevantIpIndex),
        {
          ...prevState[relevantIpIndex],
          _destroy: true
        },
        ...prevState.slice(relevantIpIndex + 1)
      ])
    }
  }

  const itemRenderer = (item, columns) => {
    return (
      <>
        { columns.map(column => {
          if (column.label === 'email') {
            return (
              <div key={ column.label } className='item flex-1'>
                <a href={ `${ usersPath }/${ item.apiUid }` }>
                  { item[column.label] }
                </a>
              </div>
            )
          } else {
            return dynamicTableItemRenderer(item, column)
          }
        })}
      </>
    )
  }

  const handleEditUserClick = (userApiUid, event) => {
    event.preventDefault()

    const relevantUser = users.find(el => el.apiUid === userApiUid)

    setAdminToBeEdited(userApiUid)
    setUpdatedName(relevantUser.name ?? '')
    setUpdatedUid(relevantUser.uid ?? '')
  }

  const handleEditAdministratorClick = (adminApiUid, event) => {
    event.preventDefault()

    const relevantAdmin = administrators.find(el => el.apiUid === adminApiUid)

    setAdminToBeEdited(adminApiUid)
    setUpdatedName(relevantAdmin.name ?? '')
    setUpdatedUid(relevantAdmin.uid ?? '')
  }

  const handleUpdateAdministrator = _e => {
    const fd = new FormData()

    fd.append('administrator_api_uid', adminToBeEdited)
    fd.append('administrator[name]', updatedName)
    fd.append('administrator[uid]', updatedUid)

    setLoading(true)

    Rails.ajax({
      type: 'PATCH',
      url: updateAdministratorPath,
      dataType: 'json',
      data: fd,
      success: res => {
        setLoading(false)
        setRenderKey(Math.random())
        setAdminToBeEdited(null)
        setUpdatedName('')
        setUpdatedUid('')
        flashMessageRef.current.show(res.message)
      },
      error: res => {
        setLoading(false)
        flashMessageRef.current.show(res.errors, 'error')
      }
    })
  }

  const administratorItemRenderer = (item, columns) => {
    return (
      <>
        { columns.map(column => {
          if (column.label === 'email') {
            return (
              <div key={ column.label } className='item flex-1'>
                <a href={ `${ branchesPath }/${ branch.apiUid }/administrators/${ item.apiUid }`}>
                  { item[column.label] }
                </a>
              </div>
            )
          } else {
            return dynamicTableItemRenderer(item, column)
          }
        })}
      </>
    )
  }

  const handleDeleteAdministratorClick = (adminApiUid, event) => {
    event.preventDefault()
    setAdministratorToBeDeleted(adminApiUid)
  }

  const handleDeleteAdministrator = _e => {
    const isUser = users.find(el => el.apiUid === administratorToBeDeleted)

    const fd = new FormData()

    if (isUser) {
      fd.append('user_api_uid', administratorToBeDeleted)
    } else {
      fd.append('administrator_api_uid', administratorToBeDeleted)
    }

    setAdministratorToBeDeleted(null)
    setLoading(true)

    Rails.ajax({
      type: 'DELETE',
      url: isUser ? deleteUsersPath : deleteAdministratorsPath,
      dataType: 'json',
      data: fd,
      success: res => {
        setLoading(false)
        setRenderKey(Math.random())
        if (isUser) {
          setUsersCountState(res.usersCount)
        } else {
          setAdministratorsCountState(res.administratorsCount)
        }
        flashMessageRef.current.show(res.message)
      }
    })
  }

  const renderBranchDetails = () => {
    let jsx

    if (canEditBranch) {
      jsx = (
        <>
          <div>
            <InputField
              className='col s12'
              label={ Partners.translations.branch.attributes.name }
            >
              <input
                type='text'
                value={ branchState.name }
                onChange={ e => setBranchState(prevState => ({ ...prevState, name: e.target.value })) }
              />
            </InputField>
          </div>

          <div>
            <InputField
              className='col s12'
              label={ Partners.translations.branch.attributes.internal_name }
            >
              <input
                type='text'
                value={ branchState.internalName }
                onChange={ e => setBranchState(prevState => ({ ...prevState, internalName: e.target.value })) }
              />
            </InputField>
          </div>

          <div>
            <InputField
              className='col s12'
              label={ Partners.translations.branch.attributes.uid }
            >
              <input
                type='text'
                value={ branchState.uid }
                onChange={ e => setBranchState(prevState => ({ ...prevState, uid: e.target.value })) }
              />
            </InputField>
          </div>

          <div>
            <InputField
              className='col s12'
              label={ Partners.translations.branch.attributes.branch_location }
            >
              <textarea
                value={ branchState.branchLocation }
                onChange={ e => setBranchState(prevState => ({ ...prevState, branchLocation: e.target.value })) }
              />
            </InputField>
          </div>
        </>
      )
    } else {
      jsx = (
        <table className='responsive-table'>
          <thead>
            <tr>
              <th>{ Partners.translations.branch.attributes.name }</th>
              <th>{ Partners.translations.branch.attributes.internal_name }</th>
              <th>{ Partners.translations.branch.attributes.uid }</th>
              <th>{ Partners.translations.branch.attributes.branch_location }</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{ branchState.name }</td>
              <td>{ branchState.internalName }</td>
              <td>{ branchState.uid }</td>
              <td>{ branchState.branchLocation }</td>
            </tr>
          </tbody>
        </table>
      )
    }

    return jsx
  }

  const tabs = [
    { key: 'users', title: Partners.translations.user.plural }
  ]

  if (!!canViewAdministrator) {
    tabs.push( { key: 'administrators', title: Partners.translations.administrator.plural } )
  }
  if (canEditWhitelistedIps) {
    tabs.push( { key: 'whitelistIps', title: Partners.translations.whitelisted_ips.plural } )
  }

  const handleReinvite = (administrator, e) => {
    e.preventDefault()

    if (!administrator.canReinvite) {
      flashMessageRef.current.show(Partners.translations.administrator.has_accepted_invite, 'error')

      return
    }

    const fd = new FormData()

    fd.append('administrator_api_uid', administrator.apiUid)

    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: reinvitePath,
      dataType: 'json',
      data: fd,
      success: res => {
        setLoading(false)
        flashMessageRef.current.show(res.message)
      },
      error: res => {
        setLoading(false)
        flashMessageRef.current.show(res.error, 'error')
      }
    })
  }

  return (
    <>
      <div className='page-wrapper-header'>
        <div style={{ width: '100%' }}>
          <div className='flex-box items-center content-space-between'>
            <div className='page-header'>
              <i className={ `fa-light ${ branchIcon }` }/>
              <h1 className='page-header-title'>
                { branchName }
              </h1>
            </div>
          </div>

          <Breadcrumbs
            breadcrumbs={ breadcrumbs }
            wrapperHeader={ false }
          />
        </div>
      </div>

      <div className='card m-b-50'>
        <div className='card-content'>
          <div className='card-body'>
            { renderBranchDetails() }
          </div>
        </div>
      </div>

      <div className='card' style={{ paddingTop: 10 }}>
        <div className='card-content'>
          <div className='card-body'>
            <Tabs
              tabs={ tabs }
              currentTab={ currentTab }
              setCurrentTab={ (tab) => setCurrentTab(tab) }
              extraClasses={ 'secondary' }
            />
          </div>
        </div>
      </div>

      { currentTab == 'administrators' &&
        <>
          <div className='page-wrapper-header'>
            <div style={{ width: '100%' }}>
              <div className='flex-box items-center content-space-between flex-wrap' style={{ gap: 20 }}>
                <div className='page-header'>
                  <i className={ `fa-light ${ administratorIcon }` }/>
                  <h3>
                    { Partners.translations.administrator.plural }
                    <span className='m-l-10'>({ administratorsCountState })</span>
                  </h3>
                </div>

                { canInviteBranchAdministrator &&
                  <CreateButton
                    onClickCreate={ () => setShowAddNewAdminModal(true) }
                    createTitle={ Partners.translations.invite_administrator }
                  />
                }
              </div>
            </div>
          </div>

          <DynamicEntityIndex
            key={ renderKey }
            initialItems={ administrators }
            setInitialItems={ setAdministrators }
            disableDelete={ true }
            entity={ 'administrators' }
            tabs={ [] }
            tableHeadings={ administratorSelectedColumns }
            disableCustomization={ true }
            itemRenderer={ administratorItemRenderer }
            itemsPath={ getAdministratorsPath }
            icon={ administratorIcon }
            noResourcesTitle={ Partners.translations.administrator.no_administrator }
            listColumns={ administratorListColumns }
            actions={ administratorActions }
            columnTranslations={ Partners.translations.columns }
            fetchOnStart={ true }
            disableUrlUpdates
          />
        </>
      }

      { currentTab == 'users' &&
        <>
          <div className='page-wrapper-header'>
            <div style={{ width: '100%' }}>
              <div className='flex-box items-center content-space-between flex-wrap' style={{ gap: 20 }}>
                <div className='page-header'>
                  <i className={ `fa-light ${ userIcon }` }/>
                  <h3>
                    { Partners.translations.user.plural }
                    <span className='m-l-10'>({ usersCountState })</span>
                  </h3>
                </div>

                { canCreateUsers &&
                  <CreateButton
                    onClickCreate={ () => setShowCreateUserModal(true) }
                    createTitle={ ReactiveCore.translations.create }
                  />
                }
              </div>
            </div>
          </div>

          <DynamicEntityIndex
            key={ renderKey }
            initialItems={ users }
            setInitialItems={ setUsers }
            disableDelete={ true }
            entity={ 'administrators' }
            tabs={ [] }
            tableHeadings={ userSelectedColumns }
            disableCustomization={ true }
            itemRenderer={ itemRenderer }
            itemsPath={ usersPath }
            icon={ userIcon }
            noResourcesTitle={ Partners.translations.user.no_users }
            listColumns={ userListColumns }
            actions={ actions }
            columnTranslations={ Partners.translations.columns }
            fetchOnStart={ true }
            disableUrlUpdates
          />
        </>
      }

      { currentTab == 'whitelistIps' &&
        <>
          <div className='page-wrapper-header'>
            <div style={{ width: '100%' }}>
              <div className='flex-box items-center content-space-between'>
                <div className='page-header'>
                  <i className='fa-light fa-ban'/>
                  <h3>
                    { Partners.translations.whitelisted_ips.plural }
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='card-body'>
              <div className='m-t-30'>
                { Partners.translations.whitelisted_ips.description }
              </div>
              <div>
                { whitelistedIps.filter(el => !el._destroy).map((whitelistedIp, index) => (
                  <div
                    key={ index }
                    className='flex-box items-center content-space-between'
                  >
                    <div>
                      <InputField
                        label={ Partners.translations.whitelisted_ips.ip }
                      >
                        <input
                          type='text'
                          value={ whitelistedIp.ip }
                          onChange={ e => handleUpdateWhitelistedIp(whitelistedIp.id, e.target.value) }
                        />
                      </InputField>
                    </div>

                    <div
                      className='flex-box items-center'
                      style={{ cursor: 'pointer' }}
                      onClick={ _e => handleDeleteWhitelistedIp(whitelistedIp.id) }
                    >
                      <i
                        className='fa-light fa-trash'
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div
                className='flex-box content-end m-t-50'
              >
                <div
                  className='btn'
                  onClick={ handleAddWhitelistedIp }
                >
                  { Partners.translations.add_new }
                </div>
              </div>
            </div>
          </div>
        </>
      }

      { showCreateUserModal &&
        <Modal
          visible={ showCreateUserModal }
          closeModal={ closeCreateUserModal }
          mode='large'
          action={ createUser }
          actionText={ ReactiveCore.translations.create }
          disableAction={ verifyFields() }
        >
          <div
            className='flex-box flex-column flex-1'
          >
            <div>
              <div className='bold' style={{ fontSize: 32 }}>
                { Partners.translations.user.create_user }
              </div>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.user.name }
              >
                <input
                  type='text'
                  value={ newUser.name }
                  onChange={ e => setNewUser(prevState => ({ ...prevState, name: e.target.value })) }
                />
              </InputField>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.administrator.attributes.email }
              >
                <input
                  type='text'
                  value={ newUser.email }
                  onChange={ e => setNewUser(prevState => ({ ...prevState, email: e.target.value })) }
                />
              </InputField>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.user.uid }
              >
                <input
                  type='text'
                  value={ newUser.uid }
                  onChange={ e => setNewUser(prevState => ({ ...prevState, uid: e.target.value })) }
                />
              </InputField>
            </div>

          </div>
        </Modal>
      }

      { showAddNewAdminModal &&
        <Modal
          visible={ showAddNewAdminModal }
          closeModal={ closeAddNewAdminModal }
          mode='large'
          action={ inviteBranchAdministrator }
          actionText={ ReactiveCore.translations.confirm }
          disableAction={ !emailValidation(newAdministratorEmail) }
          showConfirmation
          confirmationText={ Partners.translations.invite_branch_administrator_confirmation }
        >
          <div className='flex-box flex-column flex-1'>
            <div className='bold' style={{ fontSize: 32 }}>
              { Partners.translations.invite_administrator }
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.user.name }
              >
                <input
                  type='text'
                  value={ newAdministratorName }
                  onChange={ e => setNewAdministratorName(e.target.value) }
                />
              </InputField>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.organization.organization_administrator_email }
              >
                <input
                  type='text'
                  value={ newAdministratorEmail }
                  onChange={ e => setNewAdministratorEmail(e.target.value) }
                />
              </InputField>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.user.uid }
              >
                <input
                  type='text'
                  value={ newAdministratorUid }
                  onChange={ e => setNewAdministratorUid(e.target.value) }
                />
              </InputField>
            </div>

          </div>
        </Modal>
      }

      { !!adminToBeEdited &&
        <Modal
          visible={ !!adminToBeEdited }
          closeModal={ () => { setAdminToBeEdited(null); setUpdatedName(''); setUpdatedUid('') }}
          mode='medium'
          action={ handleUpdateAdministrator }
          actionText={ ReactiveCore.translations.confirm }
        >
          <div className='flex-box flex-column flex-1'>
            <div className='bold' style={{ fontSize: 32 }}>
              { Partners.translations.administrator.edit_user }
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.user.name }
              >
                <input
                  type='text'
                  value={ updatedName }
                  onChange={ e => setUpdatedName(e.target.value) }
                />
              </InputField>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Partners.translations.user.uid }
              >
                <input
                  type='text'
                  value={ updatedUid }
                  onChange={ e => setUpdatedUid(e.target.value) }
                />
              </InputField>
            </div>

          </div>
          { loading && <GlobalLoader fullPage /> }
        </Modal>
      }

      { !!administratorToBeDeleted &&
        <ModalAlert
          alert={ users.find(el => el.apiUid === administratorToBeDeleted) ? Partners.translations.destroy_user_alert : Partners.translations.destroy_administrator_alert }
          onSelect={ _e => handleDeleteAdministrator() }
          onClose={ () => setAdministratorToBeDeleted(null) }
        />
      }

      { loading && <GlobalLoader fullPage/> }

      <FlashMessage ref={ flashMessageRef } />

      <SaveFixedButton
        onSave={ handleBranchSave }
        entity={ Partners.translations.branch.plural }
        ref={ ref => setSaveButtonRef(ref) }
      />
      <GlobalImports appProps={ appProps } />
    </>
  )
}
